import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  Avatar,
  CircularProgress,
  useTheme,
  IconButton,
  Chip,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UserDetailsModal from '../../components/UserDetailsModal';
import masterService from '../../services/masterService';

// Helper function to clean and validate dropdown data
const cleanDropdownData = (data) => {
  if (!Array.isArray(data)) return [];
  
  // Remove duplicates based on id and ensure proper structure
  const seen = new Set();
  const cleaned = data.filter(item => {
    if (!item || typeof item !== 'object') return false;
    
    const id = item.id || item.value;
    if (!id || seen.has(id)) return false;
    
    seen.add(id);
    
    // Ensure item has both id and name properties
    if (!item.name && item.value) {
      item.name = item.value;
    }
    
    return true;
  });
  
  return cleaned;
};

const PurchasedProfiles = ({ theme, currentUser }) => {
  const [purchasedProfiles, setPurchasedProfiles] = useState([]);
  const [loadingPurchased, setLoadingPurchased] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const profilesPerPage = 6;
  
  // State for user details modal
  const [openModal, setOpenModal] = useState(false);
  const [detailedUser, setDetailedUser] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // State for cities data
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Function to get city name by city ID
  const getCityName = useCallback((cityId) => {
    if (!cityId || !cities.length) return null;
    
    const city = cities.find(c => 
      c.id === cityId || 
      c.id === String(cityId) || 
      String(c.id) === String(cityId)
    );
    
    return city ? city.name : null;
  }, [cities]);

  // Function to fetch cities data for all purchased profiles
  const fetchCities = useCallback(async () => {
    if (!purchasedProfiles.length) return;
    
    // Get unique states from all purchased profiles
    const uniqueStates = [...new Set(
      purchasedProfiles
        .map(purchase => purchase.user?.state)
        .filter(Boolean)
    )];
    
    if (uniqueStates.length === 0) return;
    
    setLoadingCities(true);
    try {
      // Fetch cities for all unique states
      const citiesPromises = uniqueStates.map(state => 
        masterService.getCitiesList(state).catch(() => [])
      );
      
      const citiesResults = await Promise.all(citiesPromises);
      
      // Combine all cities and remove duplicates
      const allCities = citiesResults.flat();
      setCities(cleanDropdownData(allCities));
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, [purchasedProfiles]);

  // Load purchased profiles on component mount
  useEffect(() => {
    if (currentUser?.id) {
      loadPurchasedProfiles();
    }
  }, [currentUser?.id]);

  // Fetch cities when purchased profiles change
  useEffect(() => {
    if (purchasedProfiles.length > 0) {
      fetchCities();
    }
  }, [purchasedProfiles, fetchCities]);

  // Handler for loading purchased profiles
  const loadPurchasedProfiles = async () => {
    if (!currentUser?.id) return;
    setLoadingPurchased(true);
    try {
      const profiles = await masterService.getProfilePurchasesDetails();
      console.log("Loaded purchased profiles:", profiles);
      console.log("First profile structure:", profiles[0]);
      setPurchasedProfiles(profiles);
    } catch (error) {
      console.error("Error loading purchased profiles:", error);
    } finally {
      setLoadingPurchased(false);
    }
  };

  // Handler for viewing profile details
  const handleViewProfile = async (user) => {
    setOpenModal(true);
    setLoadingDetails(true);
    try {
      const userDetails = await masterService.getUserDetails(user.id);
      setDetailedUser({ ...user, ...userDetails });
    } catch (error) {
      console.error("Error fetching user details:", error);
      setDetailedUser(user); // Fallback to basic user info
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handler for showing contact details
  const handleContactDetails = (user) => {
    const contactInfo = `
      Contact Details:
      Name: ${user?.first_name || user?.name || 'N/A'}
      Phone: ${user?.mobile || 'N/A'}
      Email: ${user?.email || 'N/A'}
    `;
    alert(contactInfo);
  };

  // Handler for closing the modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setDetailedUser(null);
  };

  // Calculate pagination
  const totalPages = Math.ceil(purchasedProfiles.length / profilesPerPage);
  const startIndex = (currentPage - 1) * profilesPerPage;
  const endIndex = startIndex + profilesPerPage;
  const currentProfiles = purchasedProfiles.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Like handler with backend call
  const handleHeartClick = async (profileId) => {
    console.log("Like clicked for profile:", profileId);
    const profile = purchasedProfiles.find((p) => p.user?.id === profileId);
    console.log("Found profile:", profile);
    
    if (!profile || !profile.user) {
      console.error("Profile not found or user data missing");
      return;
    }

    const isLiked = profile.user.is_liked_by_me || false;
    const action = isLiked ? "unliked" : "liked";
    console.log("Current like status:", isLiked, "Action:", action);

    if (!currentUser || !currentUser.id) {
      alert("Please login to like profiles.");
      return;
    }

    try {
      console.log("Calling likeUser API with:", { profileId, userId: currentUser.id, action });
      await masterService.likeUser(profileId, currentUser.id, action);

      // Update state
      setPurchasedProfiles((prevProfiles) =>
        prevProfiles.map((purchase) =>
          purchase.user?.id === profileId
            ? {
                ...purchase,
                user: {
                  ...purchase.user,
                  is_liked_by_me: !isLiked,
                  liked_count: isLiked
                    ? Math.max((purchase.user.liked_count || 1) - 1, 0)
                    : (purchase.user.liked_count || 0) + 1,
                },
              }
            : purchase
        )
      );
      console.log("State updated successfully");
    } catch (error) {
      console.error("Error updating like:", error);
      alert("Failed to update like. Please try again.");
    }
  };

  // Interest handler with backend call
  const handleInterested = async (profileId) => {
    console.log("Interest clicked for profile:", profileId);
    const profile = purchasedProfiles.find((p) => p.user?.id === profileId);
    console.log("Found profile:", profile);
    
    if (!profile || !profile.user) {
      console.error("Profile not found or user data missing");
      return;
    }

    const isInterested = profile.user.is_interested_by_me || false;
    const action = isInterested ? "not_interested" : "interested";
    console.log("Current interest status:", isInterested, "Action:", action);

    if (!currentUser || !currentUser.id) {
      alert("Please login to show interest.");
      return;
    }

    try {
      console.log("Calling interestUser API with:", { profileId, userId: currentUser.id, action });
      await masterService.interestUser(profileId, currentUser.id, action);

      // Update state
      setPurchasedProfiles((prevProfiles) =>
        prevProfiles.map((purchase) =>
          purchase.user?.id === profileId
            ? {
                ...purchase,
                user: {
                  ...purchase.user,
                  is_interested_by_me: !isInterested,
                },
              }
            : purchase
        )
      );
      console.log("Interest state updated successfully");
    } catch (error) {
      console.error("Error updating interest:", error);
      alert("Failed to update interest. Please try again.");
    }
  };

  return (
    <Box sx={{ 
      mt: 6, 
      p: { xs: 2, md: 4 }, 
      borderRadius: 6, 
      background: `linear-gradient(135deg, ${theme.palette.primary.light}11 0%, ${theme.palette.secondary?.light || '#fce4ec'}22 100%)`, 
      boxShadow: '0 8px 32px rgba(62,2,23,0.07)' 
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          Your Purchased Profiles ({purchasedProfiles.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={loadPurchasedProfiles}
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': { 
              borderColor: theme.palette.primary.dark,
              bgcolor: theme.palette.primary.light + '20'
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {loadingPurchased ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : purchasedProfiles.length > 0 ? (
        <>
          <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
            {currentProfiles.map((purchase, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
              <Card
                sx={{
                  borderRadius: 6,
                  boxShadow: "0 6px 24px rgba(62,2,23,0.10)",
                  minHeight: 400,
                  height: 400,
                  width: 320,
                  maxWidth: "100%",
                  background: `linear-gradient(120deg, ${theme.palette.primary.light}22 0%, #fff 100%)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "visible",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-10px) scale(1.03)",
                    boxShadow: "0 16px 40px rgba(62,2,23,0.13)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 4,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Avatar
                      src={
                        purchase.user?.image
                          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${purchase.user.image}`
                          : undefined
                      }
                      sx={{
                        width: 100,
                        height: 100,
                        border: `4px solid ${theme.palette.primary.main}`,
                        boxShadow: "0 4px 16px rgba(62,2,23,0.15)",
                        background: "#fff",
                        zIndex: 1,
                      }}
                    >
                      {!purchase.user?.image && (purchase.user?.first_name?.charAt(0) || purchase.user?.name?.charAt(0) || 'U')}
                    </Avatar>
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      mb: 1,
                      color: theme.palette.primary.dark,
                      textAlign: "center",
                    }}
                  >
                    {purchase.user?.name || `${purchase.user?.first_name || ""} ${purchase.user?.last_name || ""}`}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {purchase.user?.age && purchase.user.age !== 0 && purchase.user.age !== null && purchase.user.age !== "" ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PersonIcon
                          sx={{ color: theme.palette.primary.light, fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {purchase.user.age} yrs
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PersonIcon
                          sx={{ color: theme.palette.primary.light, fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Not specified
                        </Typography>
                      </Box>
                    )}
                    {purchase.user?.city && purchase.user.city !== 0 && purchase.user.city !== null && purchase.user.city !== "" ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <HomeIcon
                          sx={{ color: theme.palette.primary.light, fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {getCityName(purchase.user.city) || "Not specified"}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <HomeIcon
                          sx={{ color: theme.palette.primary.light, fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Not specified
                        </Typography>
                      </Box>
                    )}
                  </Box>


                  {/* Purchase Amount */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.main,
                        fontSize: "1.1rem",
                      }}
                    >
                      Purchased for ₹{purchase.amount}
                    </Typography>
                  </Box>

                  {/* Like and Interest Buttons - Same as RecommendedUserCard */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      my: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Like button clicked for user:", purchase.user?.id);
                          if (purchase.user?.id) {
                            handleHeartClick(purchase.user.id);
                          } else {
                            console.error("No user ID found for like button");
                          }
                        }}
                        disabled={!purchase.user?.id}
                        sx={{ p: 0.5 }}
                        title={purchase.user?.is_liked_by_me ? "Unlike" : "Like"}
                      >
                        {purchase.user?.is_liked_by_me ? (
                          <FavoriteIcon sx={{ color: "red", fontSize: 28 }} />
                        ) : (
                          <FavoriteBorderIcon sx={{ color: "red", fontSize: 28 }} />
                        )}
                      </IconButton>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                      >
                        {purchase.user?.liked_count || 0}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Interest button clicked for user:", purchase.user?.id);
                          if (purchase.user?.id) {
                            handleInterested(purchase.user.id);
                          } else {
                            console.error("No user ID found for interest button");
                          }
                        }}
                        disabled={!purchase.user?.id}
                        sx={{ p: 0.5 }}
                        title={purchase.user?.is_interested_by_me ? "Not Interested" : "Interested"}
                      >
                        {purchase.user?.is_interested_by_me ? (
                          <StarIcon
                            sx={{ color: theme.palette.primary.main, fontSize: 28 }}
                          />
                        ) : (
                          <StarBorderIcon
                            sx={{ color: theme.palette.text.secondary, fontSize: 28 }}
                          />
                        )}
                      </IconButton>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: purchase.user?.is_interested_by_me ? 600 : 400,
                          color: purchase.user?.is_interested_by_me
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        }}
                      >
                        Interested
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 2, mt: 2, width: "100%" }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleViewProfile(purchase.user)}
                      sx={{
                        flex: 1,
                        borderRadius: 4,
                        py: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.dark,
                          bgcolor: theme.palette.primary.light + '20',
                        },
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleContactDetails(purchase.user)}
                      sx={{
                        flex: 1,
                        borderRadius: 4,
                        py: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        bgcolor: theme.palette.success.main,
                        '&:hover': {
                          bgcolor: theme.palette.success.dark,
                        },
                      }}
                    >
                      Contact
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
            ))}
          </Grid>
          
          {/* Pagination Controls */}
          {purchasedProfiles.length > profilesPerPage && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              mt: 4,
              gap: 2,
              p: 2,
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 3,
              backdropFilter: 'blur(10px)'
            }}>
              <IconButton
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': { bgcolor: theme.palette.primary.dark },
                  '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              
              <Typography variant="body1" sx={{ 
                fontWeight: 600, 
                color: theme.palette.primary.main,
                minWidth: '120px',
                textAlign: 'center'
              }}>
                Page {currentPage} of {totalPages}
              </Typography>
              
              <IconButton
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': { bgcolor: theme.palette.primary.dark },
                  '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            No Profiles Purchased Yet
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Start exploring profiles to make your first purchase!
          </Typography>
        </Box>
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        open={openModal}
        onClose={handleCloseModal}
        user={detailedUser}
        theme={theme}
        loading={loadingDetails}
      />
    </Box>
  );
};

export default PurchasedProfiles;
